import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Home, Search, Calculator, FileText, ArrowLeft } from 'lucide-react';
import PageSeo from '../components/PageSeo';

const NotFound = () => {
  useEffect(() => {
    // Analytics only (SEO metadata handled by PageSeo)
    if (typeof gtag !== 'undefined') {
      gtag('event', '404_error', {
        'page_title': '404 - Page Not Found',
        'page_location': window.location.href,
        'event_category': 'error'
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 flex items-center justify-center">
      <PageSeo
        title="404 — Page Not Found | CashifyGCMart"
        description="The page you're looking for doesn't exist. Explore our gift card exchange services."
        path=""
        noindex={true}
      />
      <Helmet>
        {/* Signal soft-404 to Google's rendering pipeline so this URL is de-indexed */}
        <meta name="prerender-status-code" content="404" />
      </Helmet>
      <div className="max-w-2xl mx-auto px-4 text-center">
        {/* 404 Error Visual */}
        <div className="mb-8">
          <div className="text-8xl font-bold text-gray-300 mb-4">404</div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Oops! Page Not Found
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            The page you're looking for doesn't exist or may have been moved.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Link 
            to="/" 
            className="bg-pink-600 text-white p-4 rounded-lg hover:bg-pink-700 transition-colors flex flex-col items-center space-y-2"
          >
            <Home size={24} />
            <span className="font-semibold">Home</span>
          </Link>
          
          <Link 
            to="/rate-calculator" 
            className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition-colors flex flex-col items-center space-y-2"
          >
            <Calculator size={24} />
            <span className="font-semibold">Rate Calculator</span>
          </Link>
          
          <Link 
            to="/form-submission" 
            className="bg-green-600 text-white p-4 rounded-lg hover:bg-green-700 transition-colors flex flex-col items-center space-y-2"
          >
            <FileText size={24} />
            <span className="font-semibold">Sell Cards</span>
          </Link>
          
          <Link 
            to="/guide/how-to-sell-gift-cards" 
            className="bg-purple-600 text-white p-4 rounded-lg hover:bg-purple-700 transition-colors flex flex-col items-center space-y-2"
          >
            <Search size={24} />
            <span className="font-semibold">Help Guide</span>
          </Link>
        </div>

        {/* Popular Pages */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Popular Pages</h2>
          <div className="grid md:grid-cols-2 gap-4 text-left">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Getting Started</h3>
              <ul className="space-y-1 text-sm">
                <li><Link to="/guide/how-to-sell-gift-cards" className="text-blue-600 hover:underline">Complete Selling Guide</Link></li>
                <li><Link to="/safety-guide" className="text-blue-600 hover:underline">Safety & Security</Link></li>
                <li><Link to="/gift-card-values" className="text-blue-600 hover:underline">Current Rates</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Gift Card Brands</h3>
              <ul className="space-y-1 text-sm">
                <li><Link to="/brand/amazon" className="text-blue-600 hover:underline">Amazon Gift Cards</Link></li>
                <li><Link to="/brand/apple" className="text-blue-600 hover:underline">Apple Gift Cards</Link></li>
                <li><Link to="/brand/target" className="text-blue-600 hover:underline">Target Gift Cards</Link></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Search or Contact */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg text-left mb-8">
          <h3 className="font-semibold text-blue-800 mb-2">Looking for something specific?</h3>
          <p className="text-blue-700 text-sm mb-3">
            Can't find what you're looking for? Our support team is here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <a 
              href="mailto:support@cashifygcmart.com" 
              className="text-blue-600 hover:underline text-sm font-medium"
            >
              ✉️ Email: support@cashifygcmart.com
            </a>
          </div>
        </div>

        {/* Go Back Button */}
        <div className="flex justify-center space-x-4">
          <button 
            onClick={() => window.history.back()} 
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Go Back</span>
          </button>
          
          <Link 
            to="/" 
            className="flex items-center space-x-2 bg-pink-600 text-white px-6 py-2 rounded-lg hover:bg-pink-700 transition-colors"
          >
            <Home size={20} />
            <span>Return Home</span>
          </Link>
        </div>

        {/* SEO Content for 404 */}
        <div className="mt-12 text-left bg-gray-50 p-6 rounded-lg">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">About Cashifygcmart</h2>
          <p className="text-gray-600 text-sm">
            Even though this page wasn't found, you're still in the right place for selling gift cards! 
            Cashifygcmart offers up to 93% of face value for your unused gift cards with same-day payments. 
            We accept 100+ brands including Amazon, Apple, Target, and more. Start selling your gift cards securely today!
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFound;